import React from 'react';
import _ from 'lodash';
import {Form, Grid, Header} from 'semantic-ui-react';
import {produce} from 'immer';

import {
  toDisplayDate,
  toSysDate,
  validDate,
  getFundingStreamType,
  fetchPOPupdate,
} from 'common/utils';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {TextBoxField} from 'app-ui-componentlibrary/dist/go/components/common/FormControls';
import moment from 'moment-timezone';

const VALID_POP_DATE_FORMATS = ['MM/DD/YYYY', 'YYYY-MM-DD'];

export class PeriodOfPerformance extends React.Component {
  constructor(props) {
    super(props);
    const originalPeriodOfPerformance = _.get(props, 'originalPeriodOfPerformance', {});
    const data = _.get(props, 'data', originalPeriodOfPerformance);
    const flexPOP =  _.get(props, 'flexPOP', false);

    this.state = {
      ...data,
      flexPOP: flexPOP //flexPOP: flag for pop update when flexible pop selected from FO and AwardRec.
    };
  }

  calculateDuration = () => {
    const {startDate, endDate} = this.state;
    const {originalPeriodOfPerformance} = this.props;
    if (
      validDate(startDate, VALID_POP_DATE_FORMATS) &&
      validDate(endDate, VALID_POP_DATE_FORMATS)
    ) {
      // adding 1 days to difference for inclusion of the last end day
      const originalDuration =
        moment(originalPeriodOfPerformance.endDate).diff(
          moment(originalPeriodOfPerformance.startDate),
          'days'
        ) + 1;
      const duration = moment(endDate).diff(moment(startDate), 'days') + 1;
      this.setState({
        duration,
        originalDuration: originalDuration,
      });
    }
  };

  componentDidMount() {
    const newState = produce(this.state, (draft) => {
      if (!this.state.startDate) {
        _.set(draft, 'startDate', this.props.originalPeriodOfPerformance.startDate);
      }
      if (!this.state.endDate) {
        _.set(draft, 'endDate', this.props.originalPeriodOfPerformance.endDate);
      }
    });

    this.setState({
      ...newState
    });
    this.calculateDuration();
  }

  handleChange = (e) => {
    const {name, value} = e.target;

    const updatedState = produce(this.state, (draft) => {
      let date = validDate(value, VALID_POP_DATE_FORMATS) ? toSysDate(value) : value;
      _.set(draft, name, date);
    });
    this.setState({...updatedState}, this.calculateDuration);
  };

  handleBlur = async (toBlur) => {
    const {entityDataPath, entityType, liquidationDeadlinePath, closeoutReportingDeadlinePath, grant} = this.props;
    let {startDate, endDate, flexPOP} = this.state;

    let periodOfPerformance = {
      startDate,
      endDate,
    };
    let liquidationDeadline = null;
    let closeoutReportingDeadline = null;

    if ( 
      !flexPOP &&
      validDate(startDate, VALID_POP_DATE_FORMATS) &&
      validDate(endDate, VALID_POP_DATE_FORMATS)
    ) {
        const {fundingOpportunityId} = grant;
        const {fundingStreams, budget} = grant.payload;
        const {fedShare} = budget.share;
  
        const payloadUpdate = {
          startDate: toSysDate(startDate),
          endDate: toSysDate(endDate),
          fundingStreamType: getFundingStreamType(fundingStreams),
          configId: fundingOpportunityId,
          fedShare,
        };
        const response = await fetchPOPupdate(payloadUpdate, 'amendmentPOPStartEnd');
        periodOfPerformance = _.get(response, 'periodOfPerformance', null);
        liquidationDeadline = _.get(response, 'liquidationDeadline', null);
        closeoutReportingDeadline = _.get(response, 'closeoutReportingDeadline', null);
    }

    if (!flexPOP){
      this.props.saveManyEntityData(
        [entityDataPath, liquidationDeadlinePath, closeoutReportingDeadlinePath],
        entityType,
        [periodOfPerformance, liquidationDeadline, closeoutReportingDeadline],
        toBlur
      );
    } else{
      this.props.saveManyEntityData(
        [entityDataPath],
        entityType,
        [periodOfPerformance],
        toBlur
      );
    }
  };

  render() {
    const {
      originalPeriodOfPerformance,
      validationErrors,
      validationDirtyMap,
      validationId,
    } = this.props;

    let {startDate, endDate, duration, originalDuration, flexPOP} = this.state;

    const findError = (field) => {
      const errs = _.get(_.get(validationErrors, 0), field);
      if (errs instanceof Array) {
        return _.find(errs, (err) => !err.isValid);
      }
      return errs;
    };
    const displayError = (field) => _.get(findError(field), 'isValid') === false;
    const errorMessage = (field) => _.get(findError(field), 'message');

    const isDirty = (field) => {
      const dirtyField = _.get(validationDirtyMap, ['dirty', field], false);
      return dirtyField || (_.get(validationDirtyMap, 'dirtyPage', false) && !dirtyField);
    };

    const originalStart = toSysDate(_.get(originalPeriodOfPerformance, 'startDate'));
    const originalEnd = toSysDate(_.get(originalPeriodOfPerformance, 'endDate'));

    const startValue = startDate !== undefined ? startDate : originalStart;
    const endValue = endDate !== undefined ? endDate : originalEnd;

    const isLoaded =
      startDate !== undefined &&
      endDate !== undefined &&
      originalPeriodOfPerformance !== undefined;

    return (
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column computer={4} tablet={12} mobile={16}>
              <TextBoxField
                id="startDate"
                title={'Start date'}
                name="startDate"
                label="Start Date (MM/DD/YYYY)"
                value={toDisplayDate(startValue)}
                handleChange={this.handleChange}
                handleBlur={() => this.handleBlur(`${validationId}.startDate`)}
                maxLength={10}
                type="text"
                displayError={isDirty('startDate') && displayError('startDate')}
                errorMessages={errorMessage('startDate')}
                disable={!isLoaded}
              />
            </Grid.Column>
            <Grid.Column computer={4} tablet={12} mobile={16}>
              <TextBoxField
                id="endDate"
                title={'End date'}
                name="endDate"
                label="End Date (MM/DD/YYYY)"
                value={toDisplayDate(endValue)}
                handleChange={this.handleChange}
                handleBlur={() => this.handleBlur(`${validationId}.endDate`)}
                maxLength={10}
                type="text"
                displayError={isDirty('endDate') && displayError('endDate')}
                errorMessages={errorMessage('endDate')}
                disable={!isLoaded}
              />
            </Grid.Column>
            {duration
             ?
             <Grid.Column computer={16}>
               <div className="padding-bottom--standard">
                 <div>
                   <strong>Duration</strong>
                 </div>
                 <p>
                   {duration} {duration == 1 ? 'day' : 'days'}
                 </p>
               </div>
             </Grid.Column>
             :
             null
            }
            {!flexPOP && (startDate !== originalStart || endDate !== originalEnd) && (
              <Grid.Column
                width={16}
                className="diffingBackground striped--warning padding-top padding-bottom"
              >
                <Header as="h5">Changed in this amendment</Header>
                {startDate !== originalStart && (
                  <p>
                    Start date from <strong>{toDisplayDate(originalStart)}</strong> to{' '}
                    <strong>{toDisplayDate(startDate)}</strong>
                  </p>
                )}
                {endDate !== originalEnd && (
                  <p>
                    End date from <strong>{toDisplayDate(originalEnd)}</strong> to{' '}
                    <strong>{toDisplayDate(endDate)}</strong>
                  </p>
                )}
                {duration !== originalDuration && (
                  <p>
                    Duration from <strong>{originalDuration}</strong> days to{' '}
                    <strong>{duration}</strong> days
                  </p>
                )}
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Form>
    );
  }
}

export default connectDynamic(PeriodOfPerformance);
